import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Tabs,
	Tab,
	Card,
	CardContent,
	Button,
	Typography,
	Breadcrumbs,
	Link,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { User, UserType } from '../../gql/graphql';
import Loader from '../../components/generic/Loader';
import { ADD_UPDATE_USER, GET_USER_BY_ID } from '../../gql/queries';
import { useShopDetails } from '../../hooks/use-shop';

// Tab Components
import BasicInfoTab from '../../components/user/BasicInfoTab';
import RolesTab from '../../components/user/RolesTab';
import AdvancedTab from '../../components/user/AdvancedTab';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`user-tabpanel-${index}`}
			aria-labelledby={`user-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
}

function UserDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { shopId, locationId } = useShopDetails();
	const [currentTab, setCurrentTab] = useState(0);
	const [userData, setUserData] = useState<Partial<User>>({});
	const [isLoading, setIsLoading] = useState(false);

	const isEditMode = !!id && id !== 'new';

	const [getUserById] = useLazyQuery<Record<'user', User>>(
		GET_USER_BY_ID
	);

	const [addUpdateUser, { loading: saving }] = useMutation(ADD_UPDATE_USER, {
		onCompleted: (data) => {
			toast.success(
				`User: ${data.addUpdateUser.firstName} ${data.addUpdateUser.lastName} ${isEditMode ? 'updated' : 'created'} successfully.`
			);
			if (!isEditMode) {
				navigate(`/dashboard/users/${data.addUpdateUser._id}/edit`);
			}
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	useEffect(() => {
		if (isEditMode) {
			loadUser();
		}
	}, [id]);

	const loadUser = async () => {
		setIsLoading(true);
		try {
			const { data } = await getUserById({
				variables: { id: id!, shopId, locationId },
			});
			if (data?.user) {
				setUserData(data.user);
			}
		} catch (error: any) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleSave = async () => {
		try {
			// Validate required fields
			if (!userData.firstName || !userData.email || !userData.phone) {
				toast.error('Please fill in all required fields (First Name, Email, Phone)');
				return;
			}

			// Validate phone number
			if (userData.phone && (userData.phone.length < 10 || userData.phone.length > 10)) {
				toast.error('Phone number must be exactly 10 digits');
				return;
			}

			// Validate email
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (userData.email && !emailRegex.test(userData.email)) {
				toast.error('Please enter a valid email address');
				return;
			}

			// Ensure roles are set - add default if none exist
			const roles = userData.roles && userData.roles.length > 0
				? userData.roles
				: [{ locationId, role: UserType.LocationUser }];

			await addUpdateUser({
				variables: { 
					user: { 
						...userData, 
						roles,
						shopId 
					} 
				},
			});
		} catch (error: any) {
			toast.error(error?.message);
		}
	};

	const updateUserData = (updates: Partial<User>) => {
		setUserData((prev) => ({ ...prev, ...updates }));
	};

	return (
		<>
			<Loader isLoading={isLoading || saving} />
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					{/* Header */}
					<Box sx={{ mb: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Button
								startIcon={<ArrowBack />}
								onClick={() => navigate('/dashboard/users')}
								sx={{ mr: 2 }}
							>
								Back
							</Button>
							<Box sx={{ flexGrow: 1 }}>
								<Typography variant="h4">
									{isEditMode ? 'Edit User' : 'Create New User'}
								</Typography>
								<Breadcrumbs sx={{ mt: 1 }}>
									<Link
										underline="hover"
										color="inherit"
										onClick={() => navigate('/dashboard/users')}
										sx={{ cursor: 'pointer' }}
									>
										Users
									</Link>
									<Typography color="text.primary">
										{isEditMode ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Edit' : 'New User'}
									</Typography>
								</Breadcrumbs>
							</Box>
							<Button
								variant="contained"
								startIcon={<Save />}
								onClick={handleSave}
								disabled={saving}
							>
								Save User
							</Button>
						</Box>
					</Box>

					{/* Tabs */}
					<Card>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<Tabs
								value={currentTab}
								onChange={handleTabChange}
								variant="scrollable"
								scrollButtons="auto"
							>
								<Tab label="Basic Information" />
								<Tab label="Roles & Permissions" />
								<Tab label="Advanced" />
							</Tabs>
						</Box>

						<CardContent>
							<TabPanel value={currentTab} index={0}>
								<BasicInfoTab
									userData={userData}
									updateUserData={updateUserData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={1}>
								<RolesTab
									userData={userData}
									updateUserData={updateUserData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={2}>
								<AdvancedTab
									userData={userData}
									updateUserData={updateUserData}
								/>
							</TabPanel>
						</CardContent>
					</Card>
				</Container>
			</Box>
		</>
	);
}

export default UserDetailPage;
